import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { EntityService } from 'app/projects/entity/src/lib/services/entity.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CourseCatalog } from '../models/course-catalog.model';
import { ICourseCatalogQuery } from '../interfaces/course-catalog.query.interface';
import { CourseCatalogCommand } from '../models/course-catalog.command.model';
import { CourseCatalogOverview } from '../models/course-catalog-overview.model';
import { ICourseCatalogOverviewQuery } from '../interfaces/course-catalog-overview.query.interface';
import { CourseCatalogOverviewCategory } from '../models/course-catalog-overview-category.model';
import { ICourseCatalogOverviewCategoryQuery } from '../interfaces/course-catalog-overview-category.query.interface';
import { ICourseCatalogPostingStreamQuery } from 'app/library/course-catalog-landing-page/interfaces/course-catalog-posting-stream-query.interface';
import { CourseCatalogPostingStream } from 'app/library/course-catalog-landing-page/models/course-catalog-posting-stream.model';
import { IPaginatedResponse } from 'app/projects/subentity/src/lib/interfaces/paginated-response.interface';

export interface Pagination {
    number_of_pages: number;
    number_of_results: number;
}

@Injectable()
export class CourseCatalogApiService extends EntityService<ICourseCatalogQuery> {
    postingsPage = 1;

    private _postingsPagination: Pagination = { number_of_pages: 0, number_of_results: 0 };

    get postingsPagination(): Pagination {
        return this._postingsPagination;
    }
    set postingsPagination(value: Pagination) {
        this._postingsPagination = value;
    }

    constructor(http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) private environment: IEnvironment) {
        super(http, environment.apiUrl, 'catalogs');
    }

    getExperienceCatalog(experienceId: string): Observable<CourseCatalog> {
        return this.http.get<ICourseCatalogQuery>(`${this.apiUrl}/experiences/${experienceId}/catalog`).pipe(map((query) => new CourseCatalog(query)));
    }

    getCatalogsOverview(catalogId: string): Observable<CourseCatalogOverview> {
        return this.http.get<ICourseCatalogOverviewQuery>(`${this.apiUrl}/catalogs/${catalogId}/overview`).pipe(map((query) => new CourseCatalogOverview(query)));
    }

    getCatalogsOverviewSubcategories(categoryId: string): Observable<CourseCatalogOverviewCategory[]> {
        return this.http
            .get<ICourseCatalogOverviewCategoryQuery[]>(`${this.apiUrl}/categories/${categoryId}/sub-categories`)
            .pipe(map((queries) => queries.map((query) => new CourseCatalogOverviewCategory(query))));
    }

    getCategoryPostingStream(categoryId: string): Observable<CourseCatalogPostingStream[]> {
        return this.http
            .get<ICourseCatalogPostingStreamQuery[]>(`${this.apiUrl}/categories/${categoryId}/posting-stream`)
            .pipe(map((queries) => queries.map((query) => new CourseCatalogPostingStream(query))));
    }

    getCatalogRecentlyViewedPostingStream(catalogId: string): Observable<CourseCatalogPostingStream[]> {
        return this.http
            .get<ICourseCatalogPostingStreamQuery[]>(`${this.apiUrl}/${this.entities}/${catalogId}/recently-viewed-catalog-postings`)
            .pipe(map((queries) => queries.map((query) => new CourseCatalogPostingStream(query))));
    }

    getCatalogSuggestedSearchPostingStream(catalogId: string, searchTerm: string): Observable<CourseCatalogPostingStream[]> {
        let params: HttpParams = new HttpParams();

        if (searchTerm) {
            params = params.set('query', searchTerm);
        }

        return this.http
            .get<ICourseCatalogPostingStreamQuery[]>(`${this.apiUrl}/${this.entities}/${catalogId}/postings/suggested-search`, { params })
            .pipe(map((queries) => queries.map((query) => new CourseCatalogPostingStream(query))));
    }

    getCatalogSearchPostingStream(catalogId: string, search = '', pageSize = 10, pageIndex = 1): Observable<IPaginatedResponse<CourseCatalogPostingStream>> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (search) {
            params = params.set('query', search);
        }

        return this.http.get<IPaginatedResponse<ICourseCatalogPostingStreamQuery>>(`${this.apiUrl}/${this.entities}/${catalogId}/postings/search`, { params }).pipe(
            map((response) => {
                this.postingsPagination = {
                    number_of_pages: response.pagination.number_of_pages,
                    number_of_results: response.pagination.number_of_results,
                };

                return {
                    pagination: response.pagination,
                    data: response.data.map((item) => new CourseCatalogPostingStream(item)),
                } as IPaginatedResponse<CourseCatalogPostingStream>;
            })
        );
    }

    updateExperienceCatalog(catalogId: string, entity: CourseCatalog): Observable<CourseCatalog> {
        const data = new CourseCatalogCommand(entity);
        return this.http.patch<ICourseCatalogQuery>(`${this.apiUrl}/${this.entities}/${catalogId}`, data).pipe(map((query) => new CourseCatalog(query)));
    }

    updateCatalogCategories(catalogId: string, categoryIds: number[]): Observable<CourseCatalog> {
        const payload = {
            category_ids: categoryIds,
        };

        return this.http.put<ICourseCatalogQuery>(`${this.apiUrl}/${this.entities}/${catalogId}/categories`, payload).pipe(map((query) => new CourseCatalog(query)));
    }

    updateCatalogCategoryOrder(catalogId: string, categoryId: string, payload: { sequence: number }) {
        return this.http.patch(`${this.apiUrl}/${this.entities}/${catalogId}/categories/${categoryId}/order`, payload).toPromise();
    }
}
